// Chapter 9 - Multiple Baseline and Changing Criterion Designs
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 9.
// All content traced to source-pdfs/Chapter-9-Multiple-Baseline-and-Changing-Criterion-Designs-8.pdf

import type { ChapterModule } from "./types";

const chapter09Module: ChapterModule = {
  meta: {
    id: "09",
    number: 9,
    title: "Multiple Baseline and Changing Criterion Designs",
    subtitle: "Across behaviors / subjects / settings; probe variants",
    part: "Part 3 - Evaluating and Analyzing Behavior Change",
    estMinutes: 75,
    bacb: ["D-4", "D-5"],
  },
  curated: true,
  overview: [
    "Chapter 9 covers two designs that demonstrate experimental control WITHOUT withdrawing the IV. The MULTIPLE BASELINE design (across behaviors, settings, or subjects) staggers the introduction of the IV across data tiers and infers a functional relation from the fact that each tier changes only when the IV is applied. The CHANGING CRITERION design demonstrates control by stepwise increases in a reinforcement criterion that the behavior closely tracks.",
    "You'll learn the three basic multiple baseline forms (behaviors, settings, subjects), variations (multiple probe, delayed multiple baseline, nonconcurrent multiple baseline across participants), the rules for tier independence, the assumptions and guidelines, and the three features that combine to determine experimental control in changing criterion designs (length of phases, magnitude of criterion changes, number of criterion changes).",
  ],
  objectives: [
    { id: "D-4", text: "Describe baseline logic and apply it to multiple baseline and changing criterion designs." },
    { id: "D-5", text: "Use multiple baseline (across behaviors, settings, subjects) and changing criterion designs to demonstrate experimental control." },
  ],
  coreConcepts: [
    {
      title: "Multiple baseline core logic",
      body: [
        "In a multiple baseline design, simultaneous baseline measurement is begun on two or more tiers (behaviors, settings, or subjects). After stable baseline responding has been achieved, the IV is applied to one tier while the others remain in baseline. After maximum change is observed in the first tier, the IV is applied sequentially to the others. Experimental control is demonstrated when each tier changes WHEN, and ONLY WHEN, the IV is applied.",
      ],
    },
    {
      title: "Three basic forms",
      body: [
        "Multiple baseline across BEHAVIORS: two or more different behaviors of the same subject. Multiple baseline across SETTINGS: the same behavior of the same subject in two or more different settings. Multiple baseline across SUBJECTS: the same behavior of two or more different participants.",
      ],
    },
    {
      title: "Multiple probe design",
      body: [
        "When prolonged baseline measurement is reactive, impractical, or costly (or when later steps in a skill sequence cannot improve without earlier ones), use multiple probes. Probes are intermittent measures of all behaviors at the outset; thereafter probes are taken each time the subject masters a step. Just before instruction on a behavior begins, a short series of baseline measures is taken until stability is achieved.",
      ],
    },
    {
      title: "Delayed multiple baseline design",
      body: [
        "In a delayed multiple baseline, baseline measurement on later tiers begins AFTER baseline measurement has begun on earlier tiers. Useful when a planned reversal is no longer feasible, when resources preclude a full-scale design, or when a new behavior/setting/subject becomes available mid-study. Only baselines begun while earlier behaviors are still in baseline conditions can verify earlier-tier predictions.",
      ],
      bullets: [
        "Watch for: long wait before modifying later behaviors.",
        "Watch for: too few data points in later baselines.",
        "Watch for: masking of covariation between behaviors.",
      ],
    },
    {
      title: "Nonconcurrent multiple baseline across participants",
      body: [
        "A series of A-B sequences conducted across subjects at DIFFERENT points in time. Inherently weaker than concurrent multiple baseline because the absence of concurrent baselines means verification of intervention effects cannot be established; it only contains prediction and replication.",
      ],
    },
    {
      title: "Guidelines for multiple baseline designs",
      body: [
        "Behaviors in the design should be (a) functionally INDEPENDENT (do not covary) and (b) likely to change when the IV is applied. Tiers should be measured CONCURRENTLY and have equal opportunity to be influenced by the same variables. Do not apply the IV to the next tier until the prior tier has changed maximally AND enough time has elapsed to detect effects on other tiers. Baseline lengths should vary significantly across tiers. Apply IV first to the behavior with the most stable level or the most counter-therapeutic trend. Adding a reversal phase to one tier can strengthen the demonstration.",
      ],
    },
    {
      title: "Advantages and limitations of multiple baseline",
      body: [
        "Advantages: no withdrawal needed; sequential application parallels clinical practice; concurrent measurement allows direct monitoring of generalization; design is easy to conceptualize. Limitations: covarying behaviors can mask a true functional relation; verification must be INFERRED rather than demonstrated (weaker than reversal); design evaluates IV effectiveness more than specific behaviors; requires considerable time and resources.",
      ],
    },
    {
      title: "Changing criterion design",
      body: [
        "Used to evaluate the gradual or stepwise improvement of a behavior already in the subject's repertoire. After stable baseline, the first treatment phase requires performance to meet a specified criterion to earn reinforcement. Each subsequent phase requires an improved criterion. Experimental control is demonstrated when behavior closely conforms to the changing criteria.",
      ],
    },
    {
      title: "Range-bound and three control features",
      body: [
        "A range-bound changing criterion design specifies both a lower and an upper criterion per sub-phase. Three features combine to determine potential experimental control: (a) length of phases, (b) magnitude of criterion changes, and (c) number of criterion changes. Believability is enhanced when a previous criterion is reinstated and behavior reverses to that level.",
      ],
    },
    {
      title: "Changing criterion design considerations",
      body: [
        "Advantages: no withdrawal of treatment needed; analysis is embedded in a gradually improving behavior, parallel to practitioner goals. Limitations: target behavior must already be in the repertoire (cannot teach new skills with this design); incorporating the design's features may impede optimal learning rates.",
      ],
    },
  ],
  glossary: [
    { term: "Multiple baseline design", definition: "A single-case design in which baselines are concurrently established for two or more tiers and the IV is staggered across them.", sourceHint: "In a multiple baseline design, simultaneous baseline measurement is begun on two or more behaviors." },
    { term: "Multiple baseline across behaviors design", definition: "Multiple baseline variant in which the IV is staggered across two or more different behaviors of the same subject.", sourceHint: "a multiple baseline across behaviors design consisting of two or more different behaviors of the same subject." },
    { term: "Multiple baseline across settings design", definition: "Multiple baseline variant in which the same behavior of the same subject is measured in two or more settings.", sourceHint: "a multiple baseline across settings design consisting of the same behavior of the same subject in two or more different settings." },
    { term: "Multiple baseline across subjects design", definition: "Multiple baseline variant in which the same behavior is measured across two or more participants.", sourceHint: "a multiple baseline across subjects design consisting of the same behavior of two or more different participants." },
    { term: "Multiple probe design", definition: "A multiple baseline variant that uses intermittent probes rather than continuous baseline measurement.", sourceHint: "The multiple probe design is effective for evaluating the effects of instruction on skill sequences in which it is highly unlikely that the participant's performance on later steps in the sequence can improve without instruction or mastery of the earlier steps in the chain." },
    { term: "Delayed multiple baseline design", definition: "Multiple baseline variant in which baseline measurement on later tiers begins after baselines on earlier tiers.", sourceHint: "In a delayed multiple baseline design, baseline measurement of subsequent behaviors is begun sometime after baseline measurement began on earlier behaviors in the design." },
    { term: "Nonconcurrent multiple baseline across participants design", definition: "A series of A-B sequences conducted across subjects at different points in time; inherently weaker than concurrent variants.", sourceHint: "The nonconcurrent multiple baseline across participants design consists of a series of A-B (baseline-intervention) sequences conducted across subjects at different points in time." },
    { term: "Changing criterion design", definition: "A single-case design that evaluates stepwise improvement by raising the reinforcement criterion across successive phases.", sourceHint: "The changing criterion design can be used to evaluate the effects of a treatment on the gradual or stepwise improvement of a behavior already in the subject's repertoire." },
    { term: "Range-bound changing criterion design", definition: "Changing criterion variant in which each sub-phase specifies both a lower and an upper criterion.", sourceHint: "In a range-bound changing criterion design, each intervention sub-phase includes both a lower and an upper criterion within which the subject is expected to perform." },
  ],
  procedures: [
    {
      title: "Set up a multiple baseline across behaviors design",
      intro: "Three tiers with staggered IV introduction.",
      steps: [
        { title: "Pick functionally independent behaviors", detail: "Behaviors that do NOT covary and that are likely to respond to the same IV." },
        { title: "Start concurrent baselines", detail: "Measure all tiers simultaneously until baselines are stable." },
        { title: "Apply IV to Tier 1", detail: "When Tier 1 baseline is stable, introduce IV to it only." },
        { title: "Wait for maximum change in Tier 1", detail: "Continue measurement on Tiers 2 and 3 in baseline; ensure other tiers do not change." },
        { title: "Stagger application", detail: "Apply IV to Tier 2 only when Tier 1 has changed maximally and enough time has passed; repeat for Tier 3." },
        { title: "Vary baseline lengths", detail: "Baseline phases should differ significantly in length to support inference of experimental control." },
      ],
    },
    {
      title: "Decide between multiple baseline and multiple probe",
      intro: "Use probes when continuous baseline is impractical or reactive.",
      steps: [
        { title: "Continuous baseline is fine if...", detail: "Behavior is independently emitted and measurement is non-reactive." },
        { title: "Use multiple probe if...", detail: "Steps in a skill sequence cannot improve without prior instruction OR baseline measurement is reactive/costly." },
        { title: "Schedule probes", detail: "Initial probes on all behaviors; thereafter probe each time the subject masters one behavior; just before each new instruction, take a short series of baseline measures." },
      ],
    },
    {
      title: "Run a changing criterion design",
      intro: "Stepwise increases in criterion with behavior tracking each step.",
      steps: [
        { title: "Establish stable baseline", detail: "Measure target behavior at baseline until variability is low." },
        { title: "Set first criterion", detail: "Pick a criterion modestly above baseline; reinforce performance that meets the criterion." },
        { title: "Stay until stable", detail: "Continue this sub-phase until behavior is stable at the criterion level." },
        { title: "Raise criterion stepwise", detail: "Move to the next sub-phase with a higher criterion; repeat until target is reached." },
        { title: "Verify with reinstatement", detail: "Optionally reinstate a previous criterion to demonstrate behavior tracks the criterion back down." },
      ],
    },
  ],
  examples: [
    { scenario: "An analyst targets three social skills in the same child, staggering instruction so that each skill changes only when its turn comes.",
      application: "Multiple baseline across behaviors. Experimental control is shown by each behavior changing when, and only when, the IV is applied.",
      sourceHint: "Experimental control is demonstrated in a multiple baseline design by each behavior changing when, and only when, the independent variable is applied." },
    { scenario: "A teacher implements a self-monitoring program in three classrooms for the same student, staggered.",
      application: "Multiple baseline across SETTINGS - same behavior in different settings.",
      sourceHint: "a multiple baseline across settings design consisting of the same behavior of the same subject in two or more different settings." },
    { scenario: "A clinician teaching a multi-step cooking task probes performance on all steps initially, then probes again only when each prior step is mastered.",
      application: "Multiple probe design - well-suited for skill sequences where later steps require earlier mastery.",
      sourceHint: "The multiple probe design is effective for evaluating the effects of instruction on skill sequences in which it is highly unlikely that the participant's performance on later steps in the sequence can improve without instruction or mastery of the earlier steps in the chain." },
    { scenario: "A clinician sets a criterion of 30 minutes of exercise per day, then 40, then 50, then briefly back to 40; behavior tracks each criterion.",
      application: "Changing criterion design with reinstatement of a prior criterion strengthens the inference of experimental control.",
      sourceHint: "The believability of the changing criterion design is enhanced if a previous criterion is reinstated and the subject's behavior reverses to the level previously observed under that criterion." },
  ],
  comparisons: [
    {
      title: "Multiple baseline vs Reversal design",
      left:  { label: "Multiple baseline", points: [
        "No withdrawal of effective treatment.",
        "Useful with irreversible behaviors or non-removable IVs.",
        "Verification is INFERRED from unchanging other tiers.",
        "Time- and resource-intensive.",
      ]},
      right: { label: "Reversal", points: [
        "Demonstrates verification directly via withdrawal.",
        "Strongest functional-relation demonstration when feasible.",
        "Limited by irreversibility and ethical concerns.",
      ]},
    },
    {
      title: "Multiple baseline vs Multiple probe",
      left:  { label: "Multiple baseline", points: [
        "Continuous baseline measurement on all tiers.",
        "Best when behaviors emit independently of instruction.",
      ]},
      right: { label: "Multiple probe", points: [
        "Intermittent probe measurement.",
        "Best for skill sequences or when baseline is reactive/costly.",
        "Adds short series of baseline measures just before each instruction phase.",
      ]},
    },
    {
      title: "Concurrent vs Nonconcurrent multiple baseline across participants",
      left:  { label: "Concurrent", points: [
        "All baselines run simultaneously.",
        "Supports prediction, verification, and replication.",
      ]},
      right: { label: "Nonconcurrent", points: [
        "A-B sequences across subjects at different times.",
        "Supports only prediction + replication, not verification.",
        "Inherently weaker as a demonstration of control.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Multiple baseline is always stronger than a reversal design.",
      reality: "Multiple baseline avoids withdrawal but is inherently WEAKER for demonstrating control on any given tier because verification is inferred from non-change in other tiers.",
      sourceHint: "because verification must be inferred from the lack of change in other behaviors, the multiple baseline design is inherently weaker than the reversal design in showing experimental control between the independent variable and a given behavior.",
    },
    {
      myth: "Behaviors in a multiple baseline can be similar and still produce a clean demonstration.",
      reality: "Behaviors must be functionally INDEPENDENT - they should not covary. Covariation can mask a true functional relation.",
      sourceHint: "Behaviors that are part of multiple baseline designs should be functionally independent (i.e., they do not covary).",
    },
    {
      myth: "Skill sequences should always use continuous baseline measurement.",
      reality: "For skill chains where later steps cannot improve without earlier mastery, multiple probe is more appropriate; continuous baseline would be reactive and uninformative.",
      sourceHint: "The multiple probe design is also appropriate for situations in which prolonged baseline measurement may prove reactive, impractical, or too costly.",
    },
    {
      myth: "A changing criterion design can introduce a new behavior.",
      reality: "Changing criterion designs require the target behavior to ALREADY be in the subject's repertoire; they evaluate gradual improvement, not acquisition.",
      sourceHint: "Limitations of the changing criterion design are (a) the target behavior must already be in the subject's repertoire.",
    },
    {
      myth: "All baselines in a multiple baseline should be the same length.",
      reality: "Baseline lengths should VARY significantly across tiers to support inference of experimental control.",
      sourceHint: "The length of the baseline phases for the different behaviors making up a multiple baseline design should vary significantly.",
    },
  ],
  examChecklist: [
    "Define the three forms of multiple baseline design (behaviors, settings, subjects).",
    "Explain how experimental control is demonstrated in a multiple baseline design.",
    "State the two key assumptions about behaviors in a multiple baseline.",
    "Describe the multiple probe design and when to use it.",
    "Describe the delayed multiple baseline design and three situations that justify it.",
    "Explain why the nonconcurrent multiple baseline across participants is weaker.",
    "Describe the changing criterion design and how it demonstrates control.",
    "Name the three features that determine potential experimental control in a changing criterion design.",
    "Define the range-bound changing criterion design.",
    "List two advantages and two limitations of the multiple baseline design.",
  ],
  flashcardTerms: [
    "Multiple baseline design",
    "Multiple baseline across behaviors design",
    "Multiple baseline across settings design",
    "Multiple baseline across subjects design",
    "Multiple probe design",
    "Delayed multiple baseline design",
    "Nonconcurrent multiple baseline across participants design",
    "Changing criterion design",
    "Range-bound changing criterion design",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Experimental control in a multiple baseline design is demonstrated when:",
      options: [
        { label: "A", text: "Behavior decreases below baseline" },
        { label: "B", text: "Each tier changes when, and only when, the IV is applied" },
        { label: "C", text: "All behaviors change simultaneously" },
        { label: "D", text: "Withdrawal returns behavior to baseline" },
      ],
      answer: "B",
      explanation: "Sequential change is the signature: each behavior remains unchanged until the IV reaches it.",
      topic: "Multiple baseline logic",
      sourceHint: "Experimental control is demonstrated in a multiple baseline design by each behavior changing when, and only when, the independent variable is applied.",
    },
    {
      id: "Q2",
      question: "Which design variation is best for a multi-step skill where later steps depend on earlier mastery?",
      options: [
        { label: "A", text: "Reversal design" },
        { label: "B", text: "Multiple probe design" },
        { label: "C", text: "Alternating treatments design" },
        { label: "D", text: "Changing criterion design" },
      ],
      answer: "B",
      explanation: "Multiple probe designs use intermittent probes, ideal when continuous baseline is impractical or when later steps cannot improve without earlier mastery.",
      topic: "Multiple probe",
      sourceHint: "The multiple probe design is effective for evaluating the effects of instruction on skill sequences in which it is highly unlikely that the participant's performance on later steps in the sequence can improve without instruction or mastery of the earlier steps in the chain.",
    },
    {
      id: "Q3",
      question: "Which is a key WEAKNESS of the multiple baseline design relative to a reversal design?",
      options: [
        { label: "A", text: "It requires treatment withdrawal" },
        { label: "B", text: "It only works with reversible behaviors" },
        { label: "C", text: "Verification must be inferred from the lack of change in other tiers" },
        { label: "D", text: "It cannot be implemented with multiple subjects" },
      ],
      answer: "C",
      explanation: "Verification in a multiple baseline is INFERRED (other tiers should remain unchanged); this is inherently weaker than direct verification by withdrawal.",
      topic: "Multiple baseline limits",
      sourceHint: "because verification must be inferred from the lack of change in other behaviors, the multiple baseline design is inherently weaker than the reversal design in showing experimental control.",
    },
    {
      id: "Q4",
      question: "Which is NOT a feature that determines the potential for experimental control in a changing criterion design?",
      options: [
        { label: "A", text: "Length of phases" },
        { label: "B", text: "Magnitude of criterion changes" },
        { label: "C", text: "Number of criterion changes" },
        { label: "D", text: "Number of observers" },
      ],
      answer: "D",
      explanation: "Three features combine: length, magnitude, and number of criterion changes. Observer count is unrelated.",
      topic: "Changing criterion",
      sourceHint: "Three features combine to determine the potential for a changing criterion design to demonstrate experimental control: (a) the length of phases, (b) the magnitude of criterion changes, and (c) the number of criterion changes.",
    },
    {
      id: "Q5",
      question: "Behaviors in a multiple baseline design should be:",
      options: [
        { label: "A", text: "Functionally INDEPENDENT and likely to change when the IV is applied" },
        { label: "B", text: "Functionally LINKED so they all change together" },
        { label: "C", text: "Topographically identical" },
        { label: "D", text: "Measured by different observers" },
      ],
      answer: "A",
      explanation: "Independence (no covariation) ensures a clean staggered demonstration; the behaviors must also be likely to respond to the IV.",
      topic: "Multiple baseline guidelines",
      sourceHint: "Behaviors that are part of multiple baseline designs should be functionally independent (i.e., they do not covary) and share a reasonable likelihood that each will change when the independent variable is applied to it.",
    },
    {
      id: "Q6",
      question: "Why is the nonconcurrent multiple baseline across participants inherently weaker than a concurrent variant?",
      options: [
        { label: "A", text: "It requires too many subjects." },
        { label: "B", text: "Verification cannot be established without concurrent baselines." },
        { label: "C", text: "Practice effects are uncontrollable." },
        { label: "D", text: "Subjects must be matched on age." },
      ],
      answer: "B",
      explanation: "Without concurrent baselines, only prediction and replication are available; verification of intervention effects cannot be established.",
      topic: "Nonconcurrent variant",
      sourceHint: "The nonconcurrent multiple baseline across participants design is inherently weaker than other multiple baseline design variations. The nonconcurrent design entails two of three elements of baseline logic - prediction and replication - but the absence of concurrent baseline measures does not enable verification of intervention effects.",
    },
    {
      id: "Q7",
      question: "Which is a key LIMITATION of the changing criterion design?",
      options: [
        { label: "A", text: "It cannot teach new behaviors (target must already be in repertoire)." },
        { label: "B", text: "It requires three observers." },
        { label: "C", text: "It always uses reversals." },
        { label: "D", text: "It cannot evaluate stepwise improvement." },
      ],
      answer: "A",
      explanation: "Changing criterion evaluates improvement of an already-emitted behavior - it cannot establish acquisition.",
      topic: "Changing criterion",
      sourceHint: "Limitations of the changing criterion design are (a) the target behavior must already be in the subject's repertoire.",
    },
  ],
  reviewPrompts: [
    "Sketch a multiple baseline across behaviors with three tiers and staggered IV application.",
    "Decide between continuous baseline and multiple probe for a multi-step skill program.",
    "Identify a setting where a delayed multiple baseline would be the right choice.",
    "Explain why baseline lengths must vary across tiers in a multiple baseline design.",
    "Plan a changing criterion design for exercise duration, including the three control features.",
    "Critique a published nonconcurrent multiple baseline across participants for verification gaps.",
  ],
  recap: [
    "Multiple baseline designs (across behaviors, settings, subjects) demonstrate functional relations by staggering IV application across concurrently measured tiers. Each tier should change only when the IV reaches it; baselines should differ in length, and behaviors should be functionally independent.",
    "Multiple probe and delayed multiple baseline variants accommodate skill sequences, resource constraints, or impractical baselines. Nonconcurrent variants are weaker - they lack verification.",
    "Changing criterion designs analyze stepwise improvement of behaviors already in repertoire. Experimental control rides on the length of phases, the magnitude of criterion changes, and the number of criterion changes; an occasional reinstatement of a prior criterion strengthens the inference.",
  ],
  sourceExcerpts: [
    {
      heading: "Multiple baseline logic",
      text: "In a multiple baseline design, simultaneous baseline measurement is begun on two or more behaviors. After stable baseline responding has been achieved, the independent variable is applied to one of the behaviors while baseline conditions remain in effect for the other behavior(s). After maximum change has been noted in the first behavior, the independent variable is applied in sequential fashion to the other behaviors in the design. Experimental control is demonstrated in a multiple baseline design by each behavior changing when, and only when, the independent variable is applied.",
    },
    {
      heading: "Multiple probe design",
      text: "The multiple probe design is effective for evaluating the effects of instruction on skill sequences in which it is highly unlikely that the participant's performance on later steps in the sequence can improve without instruction or mastery of the earlier steps in the chain. The multiple probe design is also appropriate for situations in which prolonged baseline measurement may prove reactive, impractical, or too costly.",
    },
    {
      heading: "Multiple baseline guidelines",
      text: "Behaviors that are part of multiple baseline designs should be functionally independent (i.e., they do not covary) and share a reasonable likelihood that each will change when the independent variable is applied to it. The length of the baseline phases for the different behaviors making up a multiple baseline design should vary significantly. All other things being equal, the independent variable should be applied first to the behavior showing the most stable level or counter-therapeutic trend of baseline.",
    },
    {
      heading: "Changing criterion design",
      text: "After stable baseline responding has been achieved, the first treatment phase is begun, in which reinforcement is usually contingent on the subject performing at a specified level (criterion). The design entails a series of treatment phases, each requiring an improved level of performance over the previous phase. Experimental control is demonstrated when the subject's behavior closely conforms to the changing criteria. Three features combine to determine the potential for a changing criterion design to demonstrate experimental control: (a) the length of phases, (b) the magnitude of criterion changes, and (c) the number of criterion changes.",
    },
  ],
};

export default chapter09Module;
